import { SummaryGrid, SummaryGroup, SummaryHeader, SummaryRow, SummarySection } from '../../../../components';
import { CalendarDateTime } from '@internationalized/date';
import { useFormContext } from 'react-hook-form';
import { claimFields } from '../../../../config';
import { useContext } from 'react';
import { Localization } from '../../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../../context/LanguageSelectorContext';

import type { IClaim, IClaimInit } from '../../../../types';
import type { FC } from 'react';

interface ILocationDetailsProps {
    claim?: IClaimInit | IClaim | null;
    setStep: (step: number) => void;
}

export const LocationDetailsSummary: FC<ILocationDetailsProps> = ({ claim, setStep }) => {
    const formContext = useFormContext();
    const { selectedLanguage } = useContext(LanguageSelectorContext);
    const localization = new Localization(selectedLanguage);

    // TIMES
    const damageDateValue = formContext.getValues(claimFields.damageDate) as CalendarDateTime;
    const damageDate = damageDateValue ? `${damageDateValue.month}-${damageDateValue.day}-${damageDateValue.year}` : '';
    const damageTimeHour =
        damageDateValue?.hour === 0
            ? 12
            : damageDateValue?.hour > 12
            ? damageDateValue?.hour - 12
            : damageDateValue?.hour ?? 0;
    const damageTimeAMPM = damageDateValue?.hour >= 12 ? 'PM' : 'AM';
    const damageTime = damageDateValue
        ? `${damageTimeHour}:${
              damageDateValue.minute <= 9 ? `0${damageDateValue.minute}` : damageDateValue.minute
          } ${damageTimeAMPM}`
        : '';
    const discoveryDateValue = formContext.getValues(claimFields.discoveryDate) as CalendarDateTime;
    const discoveryDate = discoveryDateValue
        ? `${discoveryDateValue.month}-${discoveryDateValue.day}-${discoveryDateValue.year}`
        : '';
    const discoveryTimeHour =
        discoveryDateValue?.hour === 0
            ? 12
            : discoveryDateValue?.hour > 12
            ? discoveryDateValue?.hour - 12
            : discoveryDateValue?.hour ?? 0;
    const discoveryTimeAMPM = discoveryDateValue?.hour >= 12 ? 'PM' : 'AM';
    const discoveryTime = discoveryDateValue
        ? `${discoveryTimeHour}:${
              discoveryDateValue.minute <= 9 ? `0${discoveryDateValue.minute}` : discoveryDateValue.minute
          } ${discoveryTimeAMPM}`
        : '';
    const isDamageDateUnknown: boolean = formContext.getValues('isDamageDateUnknown') ?? false;

    // ADDRESS
    const streetAddress: string = formContext.getValues(claimFields.damageStreetAddress);
    const crossStreet: string = formContext.getValues(claimFields.damageCrossStreet) ?? '';
    const crossStreetDisplay = crossStreet.length > 0 ? crossStreet : localization.translate('dropdownOptions.na');
    const city: string = formContext.getValues(claimFields.damageCity);
    const stateName: string =
        claim?.stateList.find((s) => s.id === formContext.getValues(claimFields.damageState))?.fullName ??
        formContext.getValues(claimFields.damageState)?.name ??
        localization.translate('dropdownOptions.na');
    const zipcode: string = formContext.getValues(claimFields.damageZipCode);

    // DAMAGE TYPES
    const locationType: string =
        claim?.damageLocationTypeList?.find((l) => l.id === formContext.getValues(claimFields.damageLocationType))
            ?.name ?? localization.translate('dropdownOptions.na');
    const isDamageAerial: string = formContext.getValues(claimFields.wasDamageAerial)
        ? localization.translate('dropdownOptions.yes')
        : localization.translate('dropdownOptions.no');
    const pedPoleAmp: string =
        formContext.getValues(claimFields.facilityNumber)?.length > 0
            ? formContext.getValues(claimFields.facilityNumber)
            : localization.translate('dropdownOptions.na');
    const isDamageUnderground: string = formContext.getValues(claimFields.wasDamageUnderground)
        ? localization.translate('dropdownOptions.yes')
        : localization.translate('dropdownOptions.no');
    const depthOfDamage: string = formContext.getValues(claimFields.depthOfDamage) ?? '';
    const depthOfDamageDisplay =
        depthOfDamage.length > 0 ? depthOfDamage : localization.translate('dropdownOptions.na');

    // INTERNAL IDENTIFICATIONS
    const claimHub: string = formContext.getValues(claimFields.claimHub) ?? '';
    const claimHubDisplay = claimHub.length > 0 ? claimHub : localization.translate('dropdownOptions.na');
    const claimNode: string = formContext.getValues(claimFields.claimNode) ?? '';
    const remedyId: string = formContext.getValues(claimFields.remedyId) ?? '';
    const remedyIdDisplay = remedyId.length > 0 ? remedyId : localization.translate('dropdownOptions.na');

    // Return to the damage location step.
    const onStepClick = () => setStep(0);

    return (
        <div className={'flex flex-col'}>
            {/* STEP LINK */}
            <SummaryHeader isLink onLink={onStepClick}>
                {localization.translate('locationDetailsSummary.stepLink')}
            </SummaryHeader>

            <SummaryGroup>
                <SummarySection>
                    <SummaryGrid>
                        {/* TIMES */}
                        <SummaryRow label={localization.translate('locationDetailsSummary.damageDateLabel')}>
                            {isDamageDateUnknown ? localization.translate('dropdownOptions.unknown') : damageDate}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('locationDetailsSummary.damageTimeLabel')}>
                            {isDamageDateUnknown ? localization.translate('dropdownOptions.unknown') : damageTime}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('locationDetailsSummary.discoveryDateLabel')}>
                            {discoveryDate}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('locationDetailsSummary.discoveryTimeLabel')}>
                            {discoveryTime}
                        </SummaryRow>

                        {/* ADDRESS */}
                        <SummaryRow label={localization.translate('locationDetailsSummary.streetAddressLabel')}>
                            {streetAddress}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('locationDetailsSummary.crossStreetLabel')}>
                            {crossStreetDisplay}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('locationDetailsSummary.cityLabel')}>
                            {city}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('locationDetailsSummary.stateLabel')}>
                            {stateName}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('locationDetailsSummary.zipCodeLabel')}>
                            {zipcode}
                        </SummaryRow>
                    </SummaryGrid>
                </SummarySection>

                <SummarySection>
                    <SummaryGrid>
                        {/* DAMAGE TYPES */}
                        <SummaryRow label={localization.translate('locationDetailsSummary.locationTypeLabel')}>
                            {locationType}
                        </SummaryRow>
                        <SummaryRow
                            hideColon
                            label={localization.translate('locationDetailsSummary.aboveGroundFacilitiesLabel')}
                        >
                            {isDamageAerial}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('locationDetailsSummary.pedPoleAmpLabel')}>
                            {pedPoleAmp}
                        </SummaryRow>
                        <SummaryRow
                            hideColon
                            label={localization.translate('locationDetailsSummary.undergroundFacilitiesLabel')}
                        >
                            {isDamageUnderground}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('locationDetailsSummary.depthOfDamageLabel')}>
                            {depthOfDamageDisplay}
                        </SummaryRow>

                        {/* INTERNAL IDENTIFICATIONS */}
                        <SummaryRow label={localization.translate('locationDetailsSummary.hubLabel')}>
                            {claimHubDisplay}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('locationDetailsSummary.nodeLabel')}>
                            {claimNode}
                        </SummaryRow>
                        <SummaryRow label={localization.translate('locationDetailsSummary.outageIdLabel')}>
                            {remedyIdDisplay}
                        </SummaryRow>
                    </SummaryGrid>
                </SummarySection>
            </SummaryGroup>
        </div>
    );
};
